import { Plane, Calendar, Gauge, Wrench, ShieldCheck, DollarSign, Droplet, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { arrayUnion, collection, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { PageHeader } from '../../components/PageHeader';
import { Spinner } from '../../components/Spinner';
import { DEMO_AIRCRAFT, DEMO_FLIGHTS, DEMO_MAINTENANCE, DEMO_SQUAWKS, DEMO_TRANSACTIONS } from '../../lib/demoData';
import { isDeferred, type MxLevel } from '../../lib/maintenance';
import { MaintenanceSection } from './MaintenanceSection';
import { AircraftLogbookSection } from './AircraftLogbookSection';
import type { AircraftDoc, FlightDoc, MaintenanceItemDoc, ReadingAuditEntry, SquawkDoc, TransactionDoc } from '../../lib/types';
import { DateField } from '../../components/DateField';
import { TRACKED_REG } from '../../lib/flightTrack';

export function AdminAircraft() {
  const { demoMode, fbUser, profile } = useAuth();
  const [aircraft, setAircraft] = useState<AircraftDoc[] | null>(null);
  const [editing, setEditing] = useState<AircraftDoc | null>(null);
  // Lifetime stats read the real collections; demo mode falls back to samples.
  const [squawks, setSquawks] = useState<SquawkDoc[]>(DEMO_SQUAWKS);
  const [flights, setFlights] = useState<FlightDoc[]>(DEMO_FLIGHTS);
  const [txns, setTxns] = useState<TransactionDoc[]>(DEMO_TRANSACTIONS);
  // Hours-tracked maintenance drives the 100-hour countdown on the card. Same
  // collection the Maintenance section below subscribes to; read here so the
  // number sits with Hobbs/Tach instead of being hunted for further down.
  const [mxItems, setMxItems] = useState<MaintenanceItemDoc[]>(DEMO_MAINTENANCE);

  useEffect(() => {
    if (demoMode) {
      setAircraft(DEMO_AIRCRAFT);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'aircraft'), (snap) =>
      setAircraft(snap.docs.map((d) => d.data() as AircraftDoc))
    );
    const unsubSq = onSnapshot(collection(db, 'squawks'), (snap) =>
      setSquawks(snap.docs.map((d) => d.data() as SquawkDoc))
    );
    const unsubFl = onSnapshot(collection(db, 'flights'), (snap) =>
      setFlights(snap.docs.map((d) => d.data() as FlightDoc))
    );
    const unsubTx = onSnapshot(collection(db, 'transactions'), (snap) =>
      setTxns(snap.docs.map((d) => d.data() as TransactionDoc))
    );
    const unsubMx = onSnapshot(collection(db, 'maintenance'), (snap) =>
      setMxItems(snap.docs.map((d) => ({ ...(d.data() as MaintenanceItemDoc), itemId: d.id })))
    );
    return () => {
      unsub();
      unsubSq();
      unsubFl();
      unsubTx();
      unsubMx();
    };
  }, [demoMode]);

  async function save(patch: Partial<AircraftDoc>, reason?: string) {
    if (!editing) return;
    // The admin is the SOLE editor of the master Hobbs/tach reading. Any change
    // to currentHobbs/currentTach is recorded on aircraft.readingAudit[] (who /
    // when / why), so a post-maintenance reset or correction is fully
    // accountable — this is the sanctioned way the master reading moves
    // (including backward, which onFlightLogged's forward-only advance won't do).
    const TOL = 0.05;
    const hobbsChanged =
      typeof patch.currentHobbs === 'number' && Math.abs(patch.currentHobbs - editing.currentHobbs) > TOL;
    const tachChanged =
      typeof patch.currentTach === 'number' && Math.abs(patch.currentTach - editing.currentTach) > TOL;
    let auditEntry: ReadingAuditEntry | null = null;
    if (hobbsChanged || tachChanged) {
      const byName = `${profile?.personal?.firstName ?? ''} ${profile?.personal?.lastName ?? ''}`.trim();
      auditEntry = {
        at: new Date().toISOString(),
        byUid: fbUser?.uid ?? profile?.uid ?? 'unknown',
        ...(byName ? { byName } : {}),
        field: hobbsChanged && tachChanged ? 'both' : hobbsChanged ? 'hobbs' : 'tach',
        ...(hobbsChanged ? { fromHobbs: editing.currentHobbs, toHobbs: patch.currentHobbs } : {}),
        ...(tachChanged ? { fromTach: editing.currentTach, toTach: patch.currentTach } : {}),
        reason: reason?.trim() || 'Reading updated',
      };
    }

    if (demoMode) {
      setAircraft((curr) =>
        (curr ?? []).map((a) =>
          a.aircraftId === editing.aircraftId
            ? { ...a, ...patch, ...(auditEntry ? { readingAudit: [...(a.readingAudit ?? []), auditEntry] } : {}) }
            : a
        )
      );
      setEditing(null);
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'aircraft', editing.aircraftId), {
      ...patch,
      ...(auditEntry ? { readingAudit: arrayUnion(auditEntry) } : {}),
    });
    setEditing(null);
  }

  // Demo has no snapshot to bring a maintenance-driven change (the annual date)
  // back to the header, so the sections patch the local list directly.
  const patchDemoAircraft = demoMode
    ? (patch: Partial<AircraftDoc>) =>
        setAircraft((curr) => (curr ?? []).map((x, i) => (i === 0 ? { ...x, ...patch } : x)))
    : undefined;

  // Direct owner ground / return-to-service.
  async function setGroundedByOwner(a: AircraftDoc, grounded: boolean) {
    const reason = grounded
      ? window.prompt(`Ground ${TRACKED_REG} — reason? (shown to pilots; blocks all booking + flying)`, '')
      : null;
    if (grounded && reason === null) return; // cancelled
    const patch = grounded
      ? { groundedByOwner: true, groundedReason: reason?.trim() || 'Grounded by owner.' }
      : { groundedByOwner: false, groundedReason: '' };
    if (demoMode) {
      setAircraft((curr) => (curr ?? []).map((x) => (x.aircraftId === a.aircraftId ? { ...x, ...patch } : x)));
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'aircraft', a.aircraftId), patch);
  }

  return (
    <>
      <PageHeader title="Aircraft" subtitle="Specs, current state, and operating parameters." />
      {!aircraft ? (
        <Spinner label="Loading aircraft" />
      ) : aircraft.length === 0 ? (
        <div className="card p-8 text-center text-ink-muted">
          No aircraft configured. Run <code>npm run seed</code> to add an airplane.
        </div>
      ) : (
        <div className="space-y-6">
          {aircraft.map((a) => (
            <AircraftCard
              key={a.aircraftId}
              a={a}
              squawks={squawks}
              flights={flights}
              txns={txns}
              mxItems={mxItems}
              onEdit={() => setEditing(a)}
              onSetGrounded={(g) => setGroundedByOwner(a, g)}
            />
          ))}
          <MaintenanceSection aircraft={aircraft[0]} demoMode={demoMode} onAircraftPatch={patchDemoAircraft} />
          <AircraftLogbookSection aircraft={aircraft[0]} demoMode={demoMode} onAircraftPatch={patchDemoAircraft} />
        </div>
      )}
      {editing && <EditAircraftModal aircraft={editing} onClose={() => setEditing(null)} onSave={save} />}
    </>
  );
}

function AircraftCard({
  a,
  squawks,
  flights,
  txns,
  mxItems,
  onEdit,
  onSetGrounded,
}: {
  a: AircraftDoc;
  squawks: SquawkDoc[];
  flights: FlightDoc[];
  txns: TransactionDoc[];
  mxItems: MaintenanceItemDoc[];
  onEdit: () => void;
  onSetGrounded: (grounded: boolean) => void;
}) {
  const annual = a.annualDueDate ? new Date(a.annualDueDate) : null;
  const annualDaysAway = annual
    ? Math.round((annual.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : null;
  // Closed statuses match the squawk log's own definition of "not open".
  const CLOSED = ['resolved', 'cannot_reproduce', 'not_an_issue'];
  const openSquawks = squawks.filter((s) => s.aircraftId === a.aircraftId && !CLOSED.includes(s.status)).length;
  const lifetime = txns
    .filter((t) => t.aircraftId === a.aircraftId && t.status === 'succeeded')
    .reduce((sum, t) => sum + t.amount, 0);
  const hours = flights
    .filter((f) => f.aircraftId === a.aircraftId)
    .reduce((sum, f) => sum + (f.hoursFlown ?? 0), 0);

  return (
    <div className="card overflow-hidden">
      {/* Tail number first and explicitly white — the global heading rule colours
          h2 navy, which on this navy header made it invisible. Actions sit on
          their own row under a hairline instead of floating into the spec text,
          where the red Ground button was wrapping through the middle of it. */}
      <div className={`text-white ${a.groundedByOwner ? 'bg-red-700' : 'bg-brand'}`}>
        <div className="px-5 pt-4 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="flex min-w-0 items-center gap-2">
              <Plane size={18} className={a.groundedByOwner ? 'text-white' : 'text-sky-100'} />
              <h2 className="font-display text-2xl font-bold text-white">{a.tailNumber}</h2>
            </div>
            {a.groundedByOwner ? (
              <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[11px] font-bold uppercase tracking-wider text-red-700">
                ● Grounded
              </span>
            ) : (
              <span className="shrink-0 rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white">
                {a.status}
              </span>
            )}
          </div>

          <dl className={`mt-2 space-y-0.5 text-sm ${a.groundedByOwner ? 'text-red-50' : 'text-sky-100'}`}>
            <div>
              {a.year} {a.make} {a.model} &middot; SN {a.serialNumber}
            </div>
            {a.engineMake && (
              <div>
                {a.engineMake} {a.engineModel}
                {a.engineSerial ? ` · ENG SN ${a.engineSerial}` : ''}
              </div>
            )}
            <div>{a.homeBase}</div>
          </dl>

          {a.groundedByOwner && a.groundedReason && (
            <p className="mt-2 rounded-lg bg-black/20 px-3 py-2 text-sm font-medium">
              Grounded: {a.groundedReason}
            </p>
          )}
        </div>

        <div className="mt-4 flex gap-2 border-t border-white/10 px-5 py-3 sm:px-6">
          <button
            onClick={onEdit}
            className="rounded-lg bg-white/10 px-4 py-2 text-sm font-semibold text-white ring-1 ring-inset ring-white/20 hover:bg-white/20"
          >
            Edit
          </button>
          {a.groundedByOwner ? (
            <button
              onClick={() => onSetGrounded(false)}
              className="ml-auto rounded-lg bg-white px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-50"
            >
              Return to service
            </button>
          ) : (
            <button
              onClick={() => onSetGrounded(true)}
              className="ml-auto rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
            >
              Ground aircraft
            </button>
          )}
        </div>
      </div>

      <div className="p-5 sm:p-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Metric icon={<Gauge size={14} />} label="Hobbs" value={a.currentHobbs.toFixed(1)} />
        <Metric icon={<Gauge size={14} />} label="Tach" value={a.currentTach.toFixed(1)} />
        <Metric icon={<DollarSign size={14} />} label="Hourly rate" value={`$${a.hourlyRate}`} />
        <Metric
          icon={<Calendar size={14} />}
          label="Annual due"
          value={
            annualDaysAway != null
              ? annualDaysAway < 0
                ? `Overdue ${Math.abs(annualDaysAway)}d`
                : `${annualDaysAway}d`
              : '—'
          }
          tone={
            annualDaysAway != null && annualDaysAway < 30
              ? annualDaysAway < 7
                ? 'danger'
                : 'warning'
              : 'default'
          }
        />
      </div>

      <HundredHourPanel
        a={a}
        items={mxItems.filter((m) => m.aircraftId === a.aircraftId)}
        flights={flights.filter((f) => f.aircraftId === a.aircraftId)}
      />

      {a.oilSpec && (
        <div className="px-5 sm:px-6 pb-3 -mt-1 flex items-center gap-2 text-sm text-ink-muted">
          <Droplet size={14} className="text-amber-600" /> Oil:{' '}
          <span className="font-medium text-navy-900">{a.oilSpec}</span>
        </div>
      )}

      <div className="px-5 sm:px-6 pb-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Panel title="Billing">
          <div className="text-sm">{`$${a.hourlyRate}/Hobbs hr dry, billed per flight · no monthly minimum`}</div>
          <div className="text-xs text-ink-muted mt-1">
            Away-from-base: {a.awayFromBaseHoursPerDay} hrs per night away
          </div>
        </Panel>
        <Panel title="Lifetime">
          <div className="flex items-center gap-2 text-sm">
            <Wrench size={14} className="text-amber-600" /> {openSquawks} open squawk(s)
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Gauge size={14} className="text-navy-700" /> {hours.toFixed(1)} hrs flown
          </div>
          <div className="flex items-center gap-2 text-sm">
            <DollarSign size={14} className="text-emerald-700" /> $
            {lifetime.toLocaleString()} revenue
          </div>
          <div className="flex items-center gap-2 text-sm">
            <ShieldCheck size={14} className="text-navy-700" /> Insured value: ${a.insuredValue?.toLocaleString() ?? '—'}
          </div>
        </Panel>
      </div>
    </div>
  );
}

// The number Casey plans around: tach hours until the next hours-tracked
// maintenance item comes due (seat-rail AD, oil, a 100-hour if one is on the
// list). Tach, never Hobbs — see lib/maintenance.ts. The projection uses the
// last 60 days of logged flights, so it reflects how the airplane is actually
// being flown right now rather than a lifetime average.
const PACE_WINDOW_DAYS = 60;

function HundredHourPanel({ a, items, flights }: { a: AircraftDoc; items: MaintenanceItemDoc[]; flights: FlightDoc[] }) {
  const now = new Date();
  const due = items
    .filter((m) => !isDeferred(m, now) && (m.track === 'hours' || m.track === 'both') && typeof m.dueHobbs === 'number')
    .map((m) => ({ item: m, dueTach: m.dueHobbs as number, remaining: +((m.dueHobbs as number) - a.currentTach).toFixed(1) }))
    .sort((x, y) => x.remaining - y.remaining);

  const cutoff = now.getTime() - PACE_WINDOW_DAYS * 86400000;
  const recentTach = flights
    .filter((f) => f.date && new Date(`${f.date}T12:00:00`).getTime() >= cutoff)
    .reduce((sum, f) => sum + Math.max(0, (f.tachIn ?? 0) - (f.tachOut ?? 0)), 0);
  const hrsPerWeek = recentTach / (PACE_WINDOW_DAYS / 7);
  const projected = (remaining: number) =>
    hrsPerWeek > 0 && remaining > 0 ? new Date(now.getTime() + (remaining / hrsPerWeek) * 7 * 86400000) : null;
  // Year only when it's far enough out to be ambiguous (the ignition AD lands years away).
  const fmtDay = (d: Date) =>
    d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      ...(d.getTime() - now.getTime() > 180 * 86400000 ? { year: 'numeric' } : {}),
    });

  if (due.length === 0) {
    return (
      <div className="mx-5 mb-4 rounded-xl border border-dashed border-navy-900/15 px-4 py-3 text-sm text-ink-muted sm:mx-6">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider">
          <Wrench size={14} /> 100-hour countdown
        </div>
        <p className="mt-1">No hours-tracked maintenance on the list. Add a 100-Hour Inspection in Maintenance below and it counts down here.</p>
      </div>
    );
  }

  const head = due[0];
  const interval =
    head.item.intervalHours ??
    (typeof head.item.lastDoneHobbs === 'number' ? head.dueTach - head.item.lastDoneHobbs : 100);
  const flown = Math.max(0, Math.min(interval, interval - head.remaining));
  const pct = Math.max(0, Math.min(100, Math.round((flown / interval) * 100)));
  const level: MxLevel = head.remaining < 0 ? 3 : head.remaining <= 10 ? 2 : head.remaining <= 25 ? 1 : 0;
  const numberCls = level >= 2 ? 'text-red-700' : level === 1 ? 'text-amber-800' : 'text-navy-900';
  const barCls = level >= 2 ? 'bg-red-600' : level === 1 ? 'bg-amber-500' : 'bg-brand';
  const frameCls =
    level >= 2 ? 'border-red-200 bg-red-50/60' : level === 1 ? 'border-amber-200 bg-amber-50/60' : 'border-navy-900/10 bg-sky-50/60';
  const eta = projected(head.remaining);

  return (
    <div className={`mx-5 mb-4 rounded-xl border px-4 py-3.5 sm:mx-6 ${frameCls}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-1.5 text-xs uppercase tracking-wider text-ink-muted">
          <Wrench size={14} /> 100-hour countdown
        </div>
        <span className="text-xs tabular-nums text-ink-muted">Tach {a.currentTach.toFixed(1)}</span>
      </div>

      <div className="mt-1.5 flex flex-wrap items-baseline gap-x-2">
        <span className={`font-display text-4xl font-bold tabular-nums leading-none ${numberCls}`}>
          {Math.abs(head.remaining).toFixed(1)}
        </span>
        <span className={`text-sm font-medium ${level >= 2 ? 'text-red-700' : 'text-ink-muted'}`}>
          {head.remaining < 0 ? 'hrs OVERDUE' : 'hrs left'}
        </span>
      </div>
      <div className="mt-1 text-sm text-navy-900">
        <span className="font-medium">{head.item.label}</span>
        <span className="text-ink-muted"> · due at tach {head.dueTach.toFixed(1)}</span>
      </div>

      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-slate-200/80">
        <div className={`h-full rounded-full ${barCls}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1.5 flex flex-wrap justify-between gap-x-4 text-xs text-ink-muted">
        <span className="tabular-nums">
          {flown.toFixed(1)} of {interval} hrs flown
        </span>
        <span className="tabular-nums">
          {head.remaining < 0
            ? 'Past due — ground until done'
            : eta
            ? `≈ ${fmtDay(eta)} at ${hrsPerWeek.toFixed(1)} hr/wk`
            : `No flights in the last ${PACE_WINDOW_DAYS} days to project from`}
        </span>
      </div>

      {due.length > 1 && (
        <ul className="mt-3 divide-y divide-navy-900/5 border-t border-navy-900/10 text-sm">
          {due.slice(1).map((d) => {
            const e = projected(d.remaining);
            const cls = d.remaining < 0 ? 'text-red-700' : d.remaining <= 25 ? 'text-amber-800' : 'text-navy-900';
            return (
              <li key={d.item.itemId} className="py-2">
                <div className="flex items-baseline justify-between gap-3">
                  <span className="min-w-0 text-ink">{d.item.label}</span>
                  <span className={`shrink-0 font-medium tabular-nums ${cls}`}>
                    {d.remaining < 0 ? `${Math.abs(d.remaining).toFixed(1)} hrs over` : `${d.remaining.toFixed(1)} hrs`}
                  </span>
                </div>
                <div className="text-xs tabular-nums text-ink-muted">
                  due at tach {d.dueTach.toFixed(1)}{e ? ` · ≈ ${fmtDay(e)}` : ''}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
  tone = 'default',
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  tone?: 'default' | 'warning' | 'danger';
}) {
  const cls = tone === 'danger' ? 'text-red-700' : tone === 'warning' ? 'text-amber-800' : 'text-navy-900';
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-ink-muted flex items-center gap-1">
        {icon} {label}
      </div>
      <div className={`text-lg font-display font-semibold ${cls}`}>{value}</div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg bg-sky-50/60 border border-navy-900/5 px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-ink-muted mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function EditAircraftModal({
  aircraft,
  onClose,
  onSave,
}: {
  aircraft: AircraftDoc;
  onClose: () => void;
  onSave: (patch: Partial<AircraftDoc>, reason?: string) => void;
}) {
  const [hourlyRate, setHourlyRate] = useState(aircraft.hourlyRate);
  const [promoRate, setPromoRate] = useState(aircraft.cfiDiscountRate ?? 75);
  const [annualDue, setAnnualDue] = useState(aircraft.annualDueDate);
  const [hobbs, setHobbs] = useState(aircraft.currentHobbs);
  const [tach, setTach] = useState(aircraft.currentTach);
  const [oilSpec, setOilSpec] = useState(aircraft.oilSpec ?? '');
  // Master-reading edits require a reason (the audit trail). Surface the reason
  // field only once a reading actually moves — a rate/oil edit doesn't need one.
  const [readingReason, setReadingReason] = useState('');
  const TOL = 0.05;
  const readingChanged =
    Math.abs(hobbs - aircraft.currentHobbs) > TOL || Math.abs(tach - aircraft.currentTach) > TOL;
  const reasonMissing = readingChanged && !readingReason.trim();

  return (
    <div
      className="fixed inset-0 z-50 bg-navy-900/40 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="card max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-shrink-0 items-center justify-between border-b border-slate-100 px-6 py-4">
          <h3 className="font-display text-lg font-semibold text-navy-900">
            Edit {aircraft.tailNumber}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-2 rounded-lg p-2 text-ink-muted hover:bg-slate-100 hover:text-navy-900"
          >
            <X size={20} />
          </button>
        </div>
        <div className="min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-6 py-5">
          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <label className="label">Hourly rate ($)</label>
            <input
              type="number"
              className="input"
              value={hourlyRate}
              onChange={(e) => setHourlyRate(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="label">Promo plan rate ($/hr)</label>
            <input
              type="number"
              className="input"
              value={promoRate}
              onChange={(e) => setPromoRate(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="label">Annual due date</label>
            <DateField
              value={annualDue}
              onChange={(e) => setAnnualDue(e.target.value)}
            />
          </div>
          <div>
            <label className="label">Current Hobbs</label>
            <input
              type="number"
              step="0.1"
              className="input"
              value={hobbs}
              onChange={(e) => setHobbs(Number(e.target.value))}
            />
          </div>
          <div>
            <label className="label">Current Tach</label>
            <input
              type="number"
              step="0.1"
              className="input"
              value={tach}
              onChange={(e) => setTach(Number(e.target.value))}
            />
          </div>
          {/* Master reading is billing-critical — pilots can never type it, and
              every admin change is audited. The reason field appears only when a
              reading actually moves (a post-maintenance reset, gauge swap, or
              correction). */}
          {readingChanged && (
            <div className="sm:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
              <label className="label flex items-center gap-1.5 text-amber-900">
                <Gauge size={14} /> Why is the reading changing? (recorded in the audit trail)
              </label>
              <input
                className="input mt-1"
                value={readingReason}
                onChange={(e) => setReadingReason(e.target.value)}
                placeholder="e.g. Post-maintenance Hobbs reset after meter replacement (W/O 5102)"
                required
              />
              <div className="mt-2 flex flex-wrap gap-1.5">
                {[
                  'Post-maintenance reading update',
                  'Correcting a logging error',
                  'Hobbs/tach meter replaced',
                ].map((preset) => (
                  <button
                    key={preset}
                    type="button"
                    onClick={() => setReadingReason(preset)}
                    className="rounded-full border border-amber-300 bg-white px-2.5 py-1 text-xs font-medium text-amber-900 hover:bg-amber-100"
                  >
                    {preset}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className="label">Oil spec (shown to lessees)</label>
            <input
              className="input"
              value={oilSpec}
              onChange={(e) => setOilSpec(e.target.value)}
              placeholder="Phillips 66 Victory 100AW (SAE 50) · 8 qt · Tempest AA48108-2"
            />
          </div>
          </div>
        </div>
        <div className="flex flex-shrink-0 justify-end gap-2 border-t border-slate-100 px-6 py-4">
          <button onClick={onClose} className="btn-secondary">
            Cancel
          </button>
          <button
            disabled={reasonMissing}
            onClick={() =>
              onSave(
                {
                  hourlyRate,
                  cfiDiscountRate: promoRate,
                  annualDueDate: annualDue,
                  currentHobbs: hobbs,
                  currentTach: tach,
                  oilSpec: oilSpec.trim() || undefined,
                },
                readingChanged ? readingReason : undefined
              )
            }
            className="btn-primary"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
